import { route } from 'ziggy-js';
import { usePage } from '@inertiajs/vue3';
import type { SidebarLink } from '@/Components/sg-sidebar/sg-sidebar.component.vue';
import {
	IconDashboard,
	IconFolderAdd,
	IconOfficeBuilding,
	IconSolidTable,
	IconUserCircle,
	IconUserGroup,
	IconViewBoards,
} from '@/assets/icons';
import { trans } from 'laravel-vue-i18n';

export type PermissionType = (typeof PermissionType)[keyof typeof PermissionType];
export const PermissionType = {
	CANDIDATE_MANAGE: 'candidate_manage',
	CANDIDATE_VIEW: 'candidate_view',
	PROJECT_MANAGE: 'project_manage',
	PROJECT_VIEW: 'project_view',
	COMPANY_MANAGE: 'company_manage',
	COMPANY_VIEW: 'company_view',
	CLIENT_BACKOFFICE_ACCESS: 'client_backoffice_access',
	BACKOFFICE_ACCESS: 'backoffice_access',
	BASE_TABLE_VIEW: 'base_table_view',
	BASE_TABLE_MANAGE: 'base_table_manage',
	USER_VIEW: 'user_view',
	USER_MANAGE: 'user_manage',
	ACTIVITY_LOG_VIEW: 'activity_log_view',
	ACTIVITY_LOG_MANAGE: 'activity_log_manage',
	ACTIVITY_LOG_MANAGE_ANY: 'activity_log_manage_any',
} as const;

export const toggleSwitchLabel = (toggleSwitch: boolean): string => {
	return toggleSwitch ? trans('global.disable') : trans('global.enable');
};

export const searchString = (search: string, value: string = ''): boolean => {
	return value.toLowerCase().replace(/\s+/g, '').includes(search);
};

/**
 * A hook to check if the user has a specific permission.
 *
 * @example
 * ```ts
 * const can = useCan();
 * const canManageCandidate = can(PermissionType.CANDIDATE_MANAGE);
 * const canManageProject = can('project_manage');
 * const canManageAll = can.every([
 *   PermissionType.CANDIDATE_MANAGE,
 *   PermissionType.CANDIDATE_VIEW,
 *   PermissionType.PROJECT_MANAGE,
 * ]);
 * const canManageAny = can.some([
 *   PermissionType.CANDIDATE_MANAGE,
 *   PermissionType.CANDIDATE_VIEW,
 *   PermissionType.PROJECT_MANAGE,
 * ]);
 * // ...
 * ```
 */
export function useCan() {
	const page = usePage();

	function can(permission: PermissionType): boolean {
		const permissions = page.props.auth?.user?.permissions ?? [];
		return permissions.some((item) => item === permission);
	}

	can.every = (permissions: PermissionType[]): boolean => {
		return permissions.every((permission) => can(permission));
	};

	can.some = (permissions: PermissionType[]): boolean => {
		return permissions.some((permission) => can(permission));
	};

	return can;
}

export function createSidebarLinks(can: ReturnType<typeof useCan>): Array<SidebarLink | 'separator'> {
	if (can(PermissionType.BACKOFFICE_ACCESS)) {
		return [
			{ icon: IconDashboard, label: 'Dashboard', url: route('admin.dashboard'), isVisible: true },
			{
				icon: IconUserGroup,
				label: 'Candidates',
				url: route('admin.candidates.index'),
				isVisible: can.some([PermissionType.CANDIDATE_VIEW, PermissionType.CANDIDATE_MANAGE]),
			},
			{
				icon: IconFolderAdd,
				label: 'Projects',
				url: route('admin.projects.index'),
				isVisible: can.some([PermissionType.PROJECT_VIEW, PermissionType.PROJECT_MANAGE]),
			},
			{
				icon: IconOfficeBuilding,
				label: 'Companies',
				url: route('admin.companies.index'),
				isVisible: can.some([PermissionType.COMPANY_VIEW, PermissionType.COMPANY_MANAGE]),
			},
			{
				icon: IconViewBoards,
				label: 'Pipeline',
				url: route('admin.pipelines.index'),
				isVisible: can.some([PermissionType.CANDIDATE_VIEW, PermissionType.CANDIDATE_MANAGE]),
			},
			'separator',
			{
				icon: IconSolidTable,
				label: 'Base tables',
				url: '#',
				isVisible: can.some([PermissionType.BASE_TABLE_VIEW, PermissionType.BASE_TABLE_MANAGE]),
				dropdownLinks: [
					{ label: 'Countries', url: route('admin.countries.index') },
					{ label: 'Currencies', url: route('admin.currencies.index') },
					{ label: 'Languages', url: route('admin.languages.index') },
					{ label: 'Sectors', url: route('admin.sectors.index') },
					{ label: 'Sub-Sectors', url: route('admin.sub-sectors.index') },
					{
						label: 'Candidate Statuses',
						url: route('admin.candidate-status.index'),
					},
					{ label: 'Sources', url: route('admin.sources.index') },
					{ label: 'Functions', url: route('admin.positions.index') },
					{ label: 'Sub-Functions', url: route('admin.sub-positions.index') },
					{
						label: 'Contracts and Levels',
						url: route('admin.contract-levels.index'),
					},
					{ label: 'Benefits', url: route('admin.benefits.index') },
					{ label: 'Pillars', url: route('admin.pillars.index') },
					{ label: 'Dimensions', url: route('admin.dimensions.index') },
				],
			},
			{
				icon: IconUserCircle,
				label: 'Users',
				url: '#',
				isVisible: can.some([PermissionType.USER_VIEW, PermissionType.USER_MANAGE]),
				dropdownLinks: [
					{ label: 'Users list', url: route('admin.users.index') },
					{ label: 'Roles', url: route('admin.roles.index') },
				],
			},
			// { icon: IconCog, label: 'Settings', url: route('admin.settings.index') },
			// { icon: IconPie, label: 'Reports', url: route('admin.reports.index') },
		];
	}

	if (can(PermissionType.CLIENT_BACKOFFICE_ACCESS)) {
		return [
			{ icon: IconDashboard, label: 'Dashboard', url: route('client.dashboard'), isVisible: true },
			{
				icon: IconFolderAdd,
				label: 'Candidates',
				url: route('client.pipelines'),
				isVisible: can.some([PermissionType.PROJECT_VIEW, PermissionType.PROJECT_MANAGE]),
			},
		];
	}

	return [];
}

export const getRoutePrefix = () => (useCan()(PermissionType.BACKOFFICE_ACCESS) ? 'admin' : 'client');

export const formatRounded = (value: number) => {
	const rounded = Math.round(value * 10) / 10;

	if (Number.isInteger(rounded)) {
		return String(rounded);
	}

	return rounded.toFixed(1);
};
